import React, { useState, useEffect, useRef } from "react";
import { Container } from "react-bootstrap";
import { FaChevronDown, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css";

import "../App.css";

const Icon = FaChevronDown as unknown as React.FC;
const IconLeft = FaChevronLeft as unknown as React.FC;
const IconRight = FaChevronRight as unknown as React.FC;

const bigWidth = 340;
const bigHeight = 240;
const smallWidth = 180;
const smallHeight = 140;
const distance = bigWidth + 160; // odstęp między zdjęciami na osi X 

const Home = () => {
  const { t } = useTranslation();

  // HERO state
  const heroImages = [
    "/images/516.jpeg",
    "/images/515.jpeg",
    "/images/noresize.jpg",
  ];
  const [heroIndex, setHeroIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setHeroIndex((prev) => (prev + 1) % heroImages.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [heroImages.length]);

  // GALLERY state
  const galleryImages = [
    "https://zadyma.eu/images/articles/pizza-zadyma.jpg",
    "https://zadyma.eu/images/phocagallery/piwo-zadyma/thumbs/phoca_thumb_l_piwo-zadyma010.jpg",
    "https://zadyma.eu/images/phocagallery/piwo-zadyma/thumbs/phoca_thumb_l_piwo-zadyma002.jpg",
    "https://zadyma.eu/images/articles/pizza-zadyma.jpg",
    "https://zadyma.eu/images/phocagallery/piwo-zadyma/thumbs/phoca_thumb_l_piwo-zadyma010.jpg",
  ];

  const [galleryIndex, setGalleryIndex] = useState(0);
  const [slideDir, setSlideDir] = useState<"left" | "right" | null>(null);
  const slideTimeout = useRef<NodeJS.Timeout | null>(null);
  const ANIM_TIME = 400;

  const [animating, setAnimating] = useState(false);
  const [slideDirection, setSlideDirection] = useState<"left" | "right" | null>(
    null
  );
  const [offset, setOffset] = useState(0); // przesunięcie px w animacji

  useEffect(() => {
    slideTimeout.current = setInterval(() => {
      triggerSlide("right");
    }, 4000 + ANIM_TIME);
    return () => {
      if (slideTimeout.current) clearInterval(slideTimeout.current);
    };
  }, [galleryImages.length, slideDir]);

  const triggerSlide = (dir: "left" | "right") => {
    if (animating) return; // blokada podczas animacji
    setSlideDirection(dir);
    setAnimating(true);

    let start: number | null = null;
    const distance = 520; // odległość przesunięcia (340 + 180, szerokość obrazków)

    const step = (timestamp: number) => {
      if (!start) start = timestamp;
      const elapsed = timestamp - start;
      const progress = Math.min(elapsed / ANIM_TIME, 1);
      const moveX =
        dir === "right" ? -distance * progress : distance * progress;
      setOffset(moveX);

      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        // po animacji zmieniamy indeks i resetujemy
        setGalleryIndex((prev) =>
          dir === "right"
            ? (prev + 1) % galleryImages.length
            : (prev - 1 + galleryImages.length) % galleryImages.length
        );
        setOffset(0);
        setAnimating(false);
        setSlideDirection(null);
      }
    };

    requestAnimationFrame(step);
  };

  // LIGHTBOX
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  // SCROLL ARROW
  const scrollToContent = () => {
    const el = document.getElementById("content-section");
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 200;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  return (
    <div>
      {/* HERO SECTION */}
      <section
        style={{ position: "relative", height: "80vh", overflow: "hidden" }}
      >
        {heroImages.map((img, idx) => (
          <div
            key={idx}
            style={{
              backgroundImage: `url(${img})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              opacity: heroIndex === idx ? 1 : 0,
              transition: "opacity 1s ease-in-out",
            }}
          />
        ))}
        <div
          onClick={scrollToContent}
          className="scroll-arrow"
          style={{
            position: "absolute",
            bottom: "20px",
            left: "50%",
            transform: "translateX(-50%)",
            cursor: "pointer",
            fontSize: "3rem",
            color: "#FFF",
            zIndex: 2,
          }}
        >
          <Icon />
        </div>
      </section>

      {/* ABOUT SECTION */}
      <Container className="text-center my-5" id="content-section">
        <div className="h-100 d-flex align-items-center justify-content-center">
          <p
            className="fw-bold text-center text-wazny"
            style={{ fontSize: "75px" }}
          >
            {t("heroTitle")}
          </p>
        </div>
        <p className="text-muted">{t("heroDescription")}</p>
        <p className="text-muted">{t("heroDescription2")}</p>
        <p className="text-muted">{t("heroDescription3")}</p>
        <p className="text-muted">{t("heroDescription4")}</p>
        <p className="text-muted">{t("heroDescription5")}</p>
      </Container>

      <Container className="my-5" style={{display:"none"}}>
        <h2
          className="fw-bold text-center mb-4 text-wazny"
          style={{ fontSize: "45px" }}
        >
          {t("galleryTitle")}
        </h2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: "32px",
            justifyItems: "center",
            alignItems: "center",
            width: "100%",
            maxWidth: "1100px",
            margin: "0 auto",
          }}
        >
          {galleryImages.map((img, idx) => (
            <img
              key={idx}
              src={img}
              alt={`Zdjęcie ${idx + 1}`}
              style={{
                width: "100%",
                maxWidth: "340px",
                height: "240px",
                objectFit: "cover",
                borderRadius: "16px",
                boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
                cursor: "pointer",
                transition: "transform 0.2s",
              }}
              onClick={() => {
                setGalleryIndex(idx);
                setIsLightboxOpen(true);
              }}
            />
          ))}
        </div>
      </Container>

      {isLightboxOpen && (
        <Lightbox
          mainSrc={galleryImages[galleryIndex]}
          nextSrc={galleryImages[(galleryIndex + 1) % galleryImages.length]}
          prevSrc={
            galleryImages[
              (galleryIndex - 1 + galleryImages.length) % galleryImages.length
            ]
          }
          onCloseRequest={() => setIsLightboxOpen(false)}
          onMovePrevRequest={() =>
            setGalleryIndex(
              (galleryIndex - 1 + galleryImages.length) % galleryImages.length
            )
          }
          onMoveNextRequest={() =>
            setGalleryIndex((galleryIndex + 1) % galleryImages.length)
          }
        />
      )}
    </div>
  );
};

export default Home;
